// # Give Assignment Component for Step 1
import React, { useState, useEffect } from 'react';
import { getFirestore, doc, getDoc, updateDoc } from 'firebase/firestore';
import { useAuthStore } from '../../../auth/state';
import DashboardHeader from '../../../components/DashboardHeader';
import DashboardFooter from '../../../components/DashboardFooter';
import { FaCheckCircle, FaCheckDouble, FaTimes, FaCheck, FaExclamationCircle } from 'react-icons/fa';
import './give_assign1.css';
import { db } from '../../../auth/firebase';
import cacheManager from '../../../auth/cache_manager';  // # Import cache manager

// # Import icons
import cnsIcon from '../../../general/library/step1/step1_icons/cns.png';
import endocrineIcon from '../../../general/library/step1/step1_icons/endocrine.png';
import eyeIcon from '../../../general/library/step1/step1_icons/eye.png';
import gitIcon from '../../../general/library/step1/step1_icons/git.png';
import heartIcon from '../../../general/library/step1/step1_icons/heart.png';
import kidneyIcon from '../../../general/library/step1/step1_icons/kidney.png';
import psychiatryIcon from '../../../general/library/step1/step1_icons/psychiatry.png';
import reproductiveIcon from '../../../general/library/step1/step1_icons/reproductive-system.png';
import skinIcon from '../../../general/library/step1/step1_icons/skin.png';
import anatomyIcon from '../../../general/library/step1/step1_icons/anatomy.png';
import biochemistryIcon from '../../../general/library/step1/step1_icons/biochemistry.png';
import bloodIcon from '../../../general/library/step1/step1_icons/blood.png';
import immunologyIcon from '../../../general/library/step1/step1_icons/immunology.png';
import infectiousIcon from '../../../general/library/step1/step1_icons/infectious.png';
import lungsIcon from '../../../general/library/step1/step1_icons/lungs.png';
import musculoskeletalIcon from '../../../general/library/step1/step1_icons/musculoskeletal.png';
import pathologyIcon from '../../../general/library/step1/step1_icons/pathology.png';
import pharmaIcon from '../../../general/library/step1/step1_icons/pharma.png';
import publicHealthIcon from '../../../general/library/step1/step1_icons/public_health.png';
import revisionIcon from '../../../general/library/step1/step1_icons/revision.png';

// # Give Assignment component for Step 1
function GiveAssignment1({ isAdmin }) {
  // # Get user info from auth store
  const { user } = useAuthStore();
  
  // # Add dark mode state
  const [isDarkMode, setIsDarkMode] = useState(false);
  
  // # State for modal and form
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [assignmentDate, setAssignmentDate] = useState(new Date().toISOString().split('T')[0]);
  const [assignedChapters, setAssignedChapters] = useState({});
  const [chapterStatus, setChapterStatus] = useState({});
  const [isChapterDetailsOpen, setIsChapterDetailsOpen] = useState(false);
  const [selectedChapterDetails, setSelectedChapterDetails] = useState(null);
  const [alert, setAlert] = useState(null);
  // # Add state for target user ID and mentor status
  const [targetUserId, setTargetUserId] = useState(null);
  const [isMentor, setIsMentor] = useState(false);

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, [user]);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (user && user.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', user.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Chapter data with icons
  const chapters = [
    { id: 1, title: 'Biochemistry', icon: biochemistryIcon, color: '#FFD6E0' }, // # Light pink
    { id: 2, title: 'Immunology', icon: immunologyIcon, color: '#FFEFB5' }, // # Light yellow
    { id: 3, title: 'Microbiology', icon: infectiousIcon, color: '#D6F5D6' }, // # Light green
    { id: 4, title: 'General Pathology', icon: pathologyIcon, color: '#D6E6FF' }, // # Light blue
    { id: 5, title: 'Pharmacology', icon: pharmaIcon, color: '#E6D6FF' }, // # Light purple
    { id: 6, title: 'Public Health Sciences', icon: publicHealthIcon, color: '#FFE0CC' }, // # Light orange
    { id: 7, title: 'Anatomy & Histology', icon: anatomyIcon, color: '#D6FFFF' }, // # Light cyan
    { id: 8, title: 'Cardiovascular', icon: heartIcon, color: '#FFD6E0' }, // # Light pink
    { id: 9, title: 'Endocrine', icon: endocrineIcon, color: '#FFEFB5' }, // # Light yellow
    { id: 10, title: 'Gastrointestinal', icon: gitIcon, color: '#D6F5D6' }, // # Light green
    { id: 11, title: 'Hematology & Oncology', icon: bloodIcon, color: '#D6E6FF' }, // # Light blue
    { id: 12, title: 'Musculoskeletal & Connective Tissue', icon: musculoskeletalIcon, color: '#E6D6FF' }, // # Light purple
    { id: 13, title: 'Dermatology', icon: skinIcon, color: '#FFE0CC' }, // # Light orange
    { id: 14, title: 'Neurology', icon: cnsIcon, color: '#D6FFFF' }, // # Light cyan
    { id: 15, title: 'Special Senses', icon: eyeIcon, color: '#FFD6E0' }, // # Light pink
    { id: 16, title: 'Psychiatry', icon: psychiatryIcon, color: '#FFEFB5' }, // # Light yellow
    { id: 17, title: 'Renal', icon: kidneyIcon, color: '#D6F5D6' }, // # Light green
    { id: 18, title: 'Reproductive', icon: reproductiveIcon, color: '#D6E6FF' }, // # Light blue
    { id: 19, title: 'Respiratory', icon: lungsIcon, color: '#E6D6FF' }, // # Light purple
    { id: 20, title: 'Rapid Review', icon: revisionIcon, color: '#FFE0CC' }, // # Light orange
    { id: 21, title: 'Sketchy Micro', icon: infectiousIcon, color: '#D6FFFF' } // # Light cyan
  ];

  // # Get the correct user ID (mentee's ID when mentor is viewing)
  useEffect(() => {
    const menteeId = sessionStorage.getItem('currentViewingMenteeId');
    setTargetUserId(menteeId || user?.uid);
    setIsMentor(!!menteeId);
  }, [user?.uid]);

  // # Update fetchChapterStatus to use targetUserId
  useEffect(() => {
    const fetchChapterStatus = async () => {
      try {
        if (!targetUserId) return;
        
        const db = getFirestore();
        const menteeRef = doc(db, 's1_mentee', targetUserId);
        const menteeDoc = await getDoc(menteeRef);
        
        if (menteeDoc.exists()) {
          const assignments = menteeDoc.data()?.assignments || {};
          const assigned = {};
          const status = {};
          
          Object.keys(assignments).forEach(chapter => {
            if (assignments[chapter]?.metadata?.assigned) {
              assigned[chapter] = true;
              status[chapter] = {
                complete: assignments[chapter]?.metadata?.complete || false
              };
            }
          });
          
          setAssignedChapters(assigned);
          setChapterStatus(status);
        }
      } catch (error) {
        console.error('Error fetching assignments:', error);
      }
    };

    if (targetUserId) {
      fetchChapterStatus();
    }
  }, [targetUserId]);

  // # Handle assign button click
  const handleAssignClick = () => {
    setIsModalOpen(true);
  };

  // # Handle modal close
  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedChapter(null);
  };

  // # Handle chapter selection in modal
  const handleChapterSelect = (chapter) => {
    setSelectedChapter(chapter);
  };

  // # Handle date change
  const handleDateChange = (e) => {
    setAssignmentDate(e.target.value);
  };

  // # Handle chapter tile click
  const handleChapterClick = async (chapter) => {
    try {
      const db = getFirestore();
      const menteeRef = doc(db, 's1_mentee', targetUserId);
      const menteeDoc = await getDoc(menteeRef);
      
      if (menteeDoc.exists()) {
        const assignments = menteeDoc.data()?.assignments || {};
        const chapterData = assignments[chapter.title] || {
          metadata: {},
          corrects: {},
          mistakes: {}
        };
        
        setSelectedChapterDetails({
          ...chapter,
          data: chapterData,
          totalQuestions: Object.keys(chapterData.corrects).length + Object.keys(chapterData.mistakes).length,
          correctCount: Object.keys(chapterData.corrects).length,
          mistakeCount: Object.keys(chapterData.mistakes).length,
          correctPercent: calculatePercentage(
            Object.keys(chapterData.corrects).length,
            Object.keys(chapterData.corrects).length + Object.keys(chapterData.mistakes).length
          )
        });
        setIsChapterDetailsOpen(true);
      }
    } catch (error) {
      console.error('Error fetching chapter details:', error);
    }
  };

  // # Calculate percentage helper
  const calculatePercentage = (correct, total) => {
    if (total === 0) return 0;
    return Math.round((correct / total) * 100);
  };

  // # Show alert helper function
  const showAlert = (message, type = 'success') => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 3000); // Hide after 3 seconds
  };

  // # Update handleConfirmAssignment to use targetUserId
  const handleConfirmAssignment = async () => {
    if (!selectedChapter || !targetUserId) return;

    try {
      const db = getFirestore();
      const menteeRef = doc(db, 's1_mentee', targetUserId);

      const assignmentData = {
        metadata: {
          given_at: new Date().toISOString(),
          given_by: user.email,
          assigned: true,
          complete: false
        },
        corrects: {},
        mistakes: {}
      };

      await updateDoc(menteeRef, {
        [`assignments.${selectedChapter.title}`]: assignmentData
      });

      setAssignedChapters(prev => ({
        ...prev,
        [selectedChapter.title]: true
      }));
      setChapterStatus(prev => ({
        ...prev,
        [selectedChapter.title]: { complete: false }
      }));

      handleModalClose();
      showAlert('Chapter assigned successfully!');
    } catch (error) {
      console.error('Error creating assignment:', error);
      showAlert('Failed to create assignment. Please try again.', 'error');
    }
  };

  // # Update handleRetireChapter to use targetUserId
  const handleRetireChapter = async () => {
    if (!selectedChapterDetails || !targetUserId) return;

    try {
      const db = getFirestore();
      const menteeRef = doc(db, 's1_mentee', targetUserId);

      await updateDoc(menteeRef, {
        [`assignments.${selectedChapterDetails.title}.metadata.retired`]: true
      });

      setChapterStatus(prev => ({
        ...prev,
        [selectedChapterDetails.title]: {
          ...prev[selectedChapterDetails.title],
          retired: true
        }
      }));

      setIsChapterDetailsOpen(false);
      showAlert('Chapter retired successfully!');
    } catch (error) {
      console.error('Error retiring chapter:', error);
      showAlert('Failed to retire chapter. Please try again.', 'error');
    }
  };

  // # Update handleUnretireChapter to use targetUserId
  const handleUnretireChapter = async () => {
    if (!selectedChapterDetails || !targetUserId) return;

    try {
      const db = getFirestore();
      const menteeRef = doc(db, 's1_mentee', targetUserId);

      await updateDoc(menteeRef, {
        [`assignments.${selectedChapterDetails.title}.metadata.retired`]: false
      });

      setChapterStatus(prev => ({
        ...prev,
        [selectedChapterDetails.title]: {
          ...prev[selectedChapterDetails.title],
          retired: false
        }
      }));

      setIsChapterDetailsOpen(false);
      showAlert('Chapter unretired successfully!');
    } catch (error) {
      console.error('Error unretiring chapter:', error);
      showAlert('Failed to unretire chapter. Please try again.', 'error');
    }
  };

  // # Update handleUnassignChapter to use targetUserId
  const handleUnassignChapter = async () => {
    if (!selectedChapterDetails || !targetUserId) return;

    try {
      const db = getFirestore();
      const menteeRef = doc(db, 's1_mentee', targetUserId);

      await updateDoc(menteeRef, {
        [`assignments.${selectedChapterDetails.title}`]: {}
      });

      setAssignedChapters(prev => {
        const newState = { ...prev };
        delete newState[selectedChapterDetails.title];
        return newState;
      });

      setChapterStatus(prev => {
        const newState = { ...prev };
        delete newState[selectedChapterDetails.title];
        return newState;
      });

      setIsChapterDetailsOpen(false);
      showAlert('Chapter unassigned successfully!');
    } catch (error) {
      console.error('Error unassigning chapter:', error);
      showAlert('Failed to unassign chapter. Please try again.', 'error');
    }
  };

  return (
    <div className={`gv_asgn1_container ${isDarkMode ? 'gv_asgn1_dark_mode' : ''}`}>
      {alert && (
        <div className={`gv_asgn1_alert ${alert.type === 'success' ? 'gv_asgn1_alert_success' : 'gv_asgn1_alert_error'}`}>
          <span className="gv_asgn1_alert_icon">
            {alert.type === 'success' ? <FaCheck /> : <FaExclamationCircle />}
          </span>
          <p className="gv_asgn1_alert_message">{alert.message}</p>
        </div>
      )}
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      <div className="gv_asgn1_content">
        <div className="gv_asgn1_header">
          <h1>USMLE Step 1 Assignments {isMentor && <span className="gv_asgn1_mentor_pill">Mentor</span>}</h1>
          <p>Select a chapter to create a new assignment</p>
        </div>

        {/* # Assignment Form */}
        <div className="gv_asgn1_assignment_form">
          <h2>Create New Assignment</h2>
          <button 
            className="gv_asgn1_confirm_btn"
            onClick={handleAssignClick}
          >
            Assign New Chapter
          </button>
        </div>

        {/* # Chapters Grid */}
        <div className="gv_asgn1_chapters_grid">
          {chapters.map((chapter) => (
            <div 
              key={chapter.id}
              className="gv_asgn1_chapter_tile"
              style={{ backgroundColor: chapter.color }}
              onClick={() => handleChapterClick(chapter)}
            >
              <div className="gv_asgn1_chapter_icon">
                <img 
                  src={chapter.icon} 
                  alt={chapter.title} 
                  className="gv_asgn1_icon_image"
                />
              </div>
              <h3 className="gv_asgn1_chapter_title">{chapter.title}</h3>
              {assignedChapters[chapter.title] && (
                <div className="gv_asgn1_assigned_icon">
                  <FaCheckCircle />
                </div>
              )}
              {chapterStatus[chapter.title]?.complete && (
                <div className="gv_asgn1_complete_icon">
                  <FaCheckDouble />
                </div>
              )}
              {chapterStatus[chapter.title]?.retired && (
                <div className="gv_asgn1_retired_icon">
                  <FaTimes />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* # Chapter Details Modal */}
        {isChapterDetailsOpen && selectedChapterDetails && (
          <div className="gv_asgn1_modal_overlay">
            <div className="gv_asgn1_details_modal">
              <div className="gv_asgn1_details_header" style={{ backgroundColor: selectedChapterDetails.color + '40' }}>
                <div className="gv_asgn1_details_icon">
                  <img src={selectedChapterDetails.icon} alt={selectedChapterDetails.title} />
                </div>
                <h2>{selectedChapterDetails.title}</h2>
                <button 
                  className="gv_asgn1_close_btn"
                  onClick={() => setIsChapterDetailsOpen(false)}
                >
                  <FaTimes />
                </button>
              </div>

              <div className="gv_asgn1_details_content">
                <div className="gv_asgn1_stats_grid">
                  <div className="gv_asgn1_stat_item">
                    <h3>Total Questions</h3>
                    <p>{selectedChapterDetails.totalQuestions}</p>
                  </div>
                  <div className="gv_asgn1_stat_item">
                    <h3>Correct Answers</h3>
                    <p className="gv_asgn1_correct">{selectedChapterDetails.correctCount}</p>
                  </div>
                  <div className="gv_asgn1_stat_item">
                    <h3>Mistakes</h3>
                    <p className="gv_asgn1_mistake">{selectedChapterDetails.mistakeCount}</p>
                  </div>
                  <div className="gv_asgn1_stat_item">
                    <h3>Correct Percentage</h3>
                    <p className="gv_asgn1_percent">{selectedChapterDetails.correctPercent}%</p>
                  </div>
                </div>

                <div className="gv_asgn1_metadata">
                  <h3>Metadata</h3>
                  <div className="gv_asgn1_metadata_content">
                    <p><strong>Assigned:</strong> {selectedChapterDetails.data.metadata.assigned ? 'Yes' : 'No'}</p>
                    <p><strong>Given At:</strong> {new Date(selectedChapterDetails.data.metadata.given_at).toLocaleDateString()}</p>
                    <p><strong>Given By:</strong> {selectedChapterDetails.data.metadata.given_by}</p>
                    <p><strong>Status:</strong> {selectedChapterDetails.data.metadata.retired ? 'Retired' : 'Active'}</p>
                  </div>
                </div>

                <div className="gv_asgn1_details_actions">
                  {assignedChapters[selectedChapterDetails.title] && (
                    <>
                      {chapterStatus[selectedChapterDetails.title]?.retired ? (
                        <button 
                          className="gv_asgn1_unretire_btn"
                          onClick={handleUnretireChapter}
                        >
                          Unretire Chapter
                        </button>
                      ) : (
                        <button 
                          className="gv_asgn1_retire_btn"
                          onClick={handleRetireChapter}
                        >
                          Retire Chapter
                        </button>
                      )}
                      <button 
                        className="gv_asgn1_unassign_btn"
                        onClick={handleUnassignChapter}
                      >
                        Unassign Chapter
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* # Assignment Modal */}
        {isModalOpen && (
          <div className="gv_asgn1_modal_overlay">
            <div className="gv_asgn1_modal">
              <div className="gv_asgn1_modal_header">
                <h2>Select Chapter to Assign</h2>
              </div>
              
              <div className="gv_asgn1_chapter_list">
                {chapters
                  .filter(chapter => !assignedChapters[chapter.title]) // # Filter out assigned chapters
                  .map((chapter) => (
                    <div
                      key={chapter.id}
                      className={`gv_asgn1_chapter_option ${selectedChapter?.id === chapter.id ? 'selected' : ''}`}
                      onClick={() => handleChapterSelect(chapter)}
                      style={{ backgroundColor: selectedChapter?.id === chapter.id ? `${chapter.color}40` : 'transparent' }}
                    >
                      <div className="gv_asgn1_chapter_icon">
                        <img src={chapter.icon} alt={chapter.title} className="gv_asgn1_icon_image" />
                      </div>
                      <span>{chapter.title}</span>
                    </div>
                  ))}
                {chapters.filter(chapter => !assignedChapters[chapter.title]).length === 0 && (
                  <div className="gv_asgn1_no_chapters">
                    <p>All chapters have been assigned</p>
                  </div>
                )}
              </div>

              <div className="gv_asgn1_date_picker">
                <label htmlFor="assignmentDate">Assignment Date</label>
                <input
                  type="date"
                  id="assignmentDate"
                  value={assignmentDate}
                  onChange={handleDateChange}
                />
              </div>

              <div className="gv_asgn1_modal_buttons">
                <button 
                  className="gv_asgn1_cancel_btn"
                  onClick={handleModalClose}
                >
                  Cancel
                </button>
                <button 
                  className="gv_asgn1_confirm_btn"
                  onClick={handleConfirmAssignment}
                  disabled={!selectedChapter}
                >
                  Confirm Assignment
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
}

export default GiveAssignment1;
